import React, { useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router";
import axios from "axios";
import {
    Alert,
    Badge,
    Box,
    useMediaQuery,
    useTheme,
    SelectChangeEvent,
} from "@mui/material";
import dayjs from "dayjs";
import { useIsAuthenticated } from "@azure/msal-react";

import type { TriggerKey, RefreshTriggers } from "../../models/ArtworkDataTriggers";
import { Artwork, ArtworkStub } from "../../models/Artwork";
import { Artist } from "../../models/artist";
import { ArtworkStatus, ArtworkStatusRefData } from "../../models/ArtworkStatus";
import { ArtworkProvenance } from "../../models/ArtworkProvenance";
import type { AppCategory } from "../../models/Category";

import { useReferenceData } from "../../components/ReferenceDataContext";
import { useFilter } from "../../components/Filter/ArtworkFilterContext";
import { useUser } from "../../components/UserContext";
import { createApiClient, useApiClient } from "../../components/useApiClient";
import { useMovementsApi } from "../../hooks/useMovementsApi";
import { useSafeRequest } from "../../components/useSafeRequest";
import { formatDateOnly } from "../../functions/dateFormatter";
import { DimensionDisplayType } from "../../models/UserProfile";

import ArtworkNavigation from "../../components/ArtworkNavigation/ArtworkNavigation";
import ArtworkManagement from "../../components/ArtworkManagement/ArtworkManagement";
import { ConfirmDeleteArtworkDialog } from "../../components/ArtworkManagement/ConfirmDeleteArtworkDialog";
import ArtworkStatusPanel from "../../components/ArtworkStatus/ArtworkStatusPanel";
import ArtworkNotes from "../../components/ArtworkNotes/ArtworkNotes";
import ArtworkReferencesTabs from "../../components/ArtworkReference/ArtworkReferencesTabs";
import ValuationsTabContent from "../../components/Valuations/ValuationsTabContent";
import FileUploader from "../../components/FileUploader";
import PaymentsTab from "../../components/Payments/PaymentsTab";
import { ProvenanceForm } from "../../components/Provenance/ProvenanceForm";

import ArtworkPageSkeleton from "./ArtworkPageSkeleton";
import ResponsiveDetailLayout from "../../components/Layout/ResponsiveDetailLayout";
import ArtworkHeaderSection from "../../components/ArtworkDetails/ArtworkHeaderSection";
import ArtworkPrimaryMediaSection from "../../components/ArtworkDetails/ArtworkPrimaryMediaSection";
import ArtworkDetailsSection from "../../components/ArtworkDetails/ArtworkDetailsSection";
import ArtworkSupportingSection from "../../components/ArtworkDetails/ArtworkSupportingSection";
import type { ArtworkSupportingTab, TabAction } from "../../components/ArtworkDetails/ArtworkSupportingSection";
import type { ArtworkReference, ArtworkReferenceType } from "../../models/ArtworkReference";
import type { ProvenanceMode } from "../../components/Provenance/ProvenanceForm";

//Quick Facts Header
import ArtworkQuickFactsPanel from "../../components/ArtworkDetails/ArtworkQuickFactsPanel";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';

//Icons
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from "@mui/icons-material/Edit";

//Dialogs
import AddValuationDialog from "../../components/Valuations/AddValuationDialog";
import ArtworkPaymentScheduleDialog from "../../components/Payments/ArtworkPaymentScheduleDialog";
import ArtworkReferencePanel from "../../components/ArtworkReference/ArtworkReferencePanel";
import CreateMovementModal from "../../components/Movements/CreateMovementModal";

//Dimensions
import { formatMeasurement, ArtworkMeasurementType, DimensionsPreviewStruct } from "../../models/ArtworkMeasurement";

import "../ArtworkDetails/ArtworkDetails.css";
import "../../components/StyleSheets/Visibility.css";

export interface ArtworkRouteState {
    allArtworks: ArtworkStub[];
    currentIndex?: number;
    scrollPosition?: number;
}

type ArtworkDraft = {
    artworkId: number;
    title?: string;
    categoryId: number | null;
};

type DocumentDetails = {
    id: string;
    title: string;
    description: string;
    contentType: string;
    sizeBytes: number;
    downloadUrl: string;
};

type ListResponse = {
    documents: DocumentDetails[];
};

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ArtworkPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const artworkId = Number(id ?? 0);

    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isAuthenticated = useIsAuthenticated();

    const { filteredArtworks, artworks, currentArtworkId, setCurrentArtworkId } = useFilter();
    const navigationArtworks = filteredArtworks.length > 0 ? filteredArtworks : artworks;

    const { categories, errorAny, refresh } = useReferenceData();
    const { activeClientId, getAccessToken, isAdmin, isOwner, isValuer, isFinance, activeTenantId, activeClientLicense, dimensionDisplayPreference, dimensionUnitPreference } = useUser();

    const isViewOnly = !activeClientLicense || activeClientLicense.licenseExpired || !activeClientLicense.canAddArtworks;
    const canEditArtwork = (isAdmin || isOwner || isValuer) && !isViewOnly;
    const hasFinanceRole = (isAdmin || isOwner || isFinance);

    const previousClientId = useRef<number | null>(activeClientId);
    const clientIdRef = useRef<number | null>(null);

    React.useEffect(() => {
        clientIdRef.current = activeClientId;
    }, [activeClientId]);

    React.useEffect(() => {
        if (id) {
            setCurrentArtworkId(Number(id));
        }
    }, [id, setCurrentArtworkId]);

    const { put, post, delete: del } = useApiClient();
    const movementApi = useMovementsApi();

    const [tabValue, setTabValue] = useState(0);
    const [error, setError] = useState<string | null>(null);

    const [artwork, setArtwork] = useState<Artwork | null>(null);
    const [artists, setArtists] = useState<Artist[]>([]);
    const [artworkStatus, setArtworkStatus] = useState<ArtworkStatus | null>(null);
    const [documentsList, setDocumentsList] = useState<ListResponse | null>(null);
    const [statuses, setStatuses] = useState<ArtworkStatusRefData[]>([]);

    const [firstLoadSupportingData, setFirstLoadSupportingData] = useState(true);
    const [artworkUpdated, setArtworkUpdated] = useState(0);
    const [reloadValuationsToken, setReloadValuationsToken] = useState(0);
    const [reloadArtworkDetailsToken, setReloadArtworkDetailsToken] = useState(0);
    const [reloadPaymentsToken, setReloadPaymentsToken] = useState(0);
    const [reloadStatusToken, setReloadStatusToken] = useState(0);
    const [openEditArtworkModal, setOpenEditArtworkModal] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showCreate, setShowCreate] = useState(false);

    const [artworkUpdate] = useState<ArtworkDraft>({
        artworkId,
        categoryId: null,
    });


    const api = useMemo(
        () =>
            createApiClient(
                {
                    getAccessToken,
                    getClientId: () => clientIdRef.current,
                    getTenantId: () => activeTenantId,
                },
                { timeoutMs: 12000 }
            ),
        [getAccessToken, activeTenantId]
    );

    const artworkReq = useSafeRequest<any>(api, { maxAttempts: 3 });
    const documentsReq = useSafeRequest<any>(api, { maxAttempts: 3 });
    const statusReq = useSafeRequest<any>(api, { maxAttempts: 3 });
    const artistReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });
    const movementReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });

    const getArtwork = async () => {
        const result = await artworkReq.run({
            method: "GET",
            url: `artworks/${artworkId}`,
        });

        setArtwork(result);
    };

    const getSupportingInformation = async () => {

        if (firstLoadSupportingData) {

            refresh();

            const tasks = [
                documentsReq.run({ method: "GET", url: `artworks/documents/list/${artworkId}` }),
                statusReq.run({ method: "GET", url: `artworks/${artworkId}/status` }),
                artistReq.run({ method: "GET", url: `artists/for-client/${clientIdRef.current}` }),
                movementReq.run({ method: "GET", url: `artwork-statuses` }),
            ];

            const [d, s, ar, mv] = await Promise.allSettled(tasks);

            if (d.status === "fulfilled") setDocumentsList(d.value);
            if (s.status === "fulfilled") setArtworkStatus(s.value);
            if (ar.status === "fulfilled") setArtists(ar.value);
            if (mv.status === "fulfilled") setStatuses(mv.value);

            setFirstLoadSupportingData(false);
        }
        else {
            // Just refresh status
            console.log("Refreshing status for artworkId:", artworkId);

            const tasks = [
                statusReq.run({ method: "GET", url: `artworks/${artworkId}/status` }),
            ];

            const [s] = await Promise.allSettled(tasks);

            if (s.status === "fulfilled") setArtworkStatus(s.value);
        }
    };

    React.useEffect(() => {
        if (!isAuthenticated || activeClientId == null || !artworkId) return;

        const fetchArtwork = async () => {
            await getArtwork().catch(() => { });
            await getSupportingInformation().catch(() => { });
        };

        fetchArtwork();
    }, [isAuthenticated, activeClientId, artworkUpdated, artworkId, currentArtworkId]);

    React.useEffect(() => {
        if (!isAuthenticated || activeClientId == null || !artworkId) return;

        const refreshArtworkDetails = async () => {
            await getArtwork().catch(() => { });
        };

        refreshArtworkDetails();
    }, [reloadArtworkDetailsToken]);

    React.useEffect(() => {
        if (previousClientId.current !== null && previousClientId.current !== activeClientId) {
            setOpenEditArtworkModal(false);
            navigate("/");
        }
        previousClientId.current = activeClientId;
    }, [activeClientId, navigate]);

    const documents = useMemo(() => documentsList?.documents ?? [], [documentsList]);

    const makeParentIndex = (flat: AppCategory[]) => {
        const parent = new Map<number, number | null>();
        const label = new Map<number, string>();

        for (const c of flat) {
            parent.set(c.id, c.parentId ?? null);
            label.set(c.id, c.name);
        }

        return { parent, label };
    };

    const { parentIndex, labelIndex } = useMemo(() => {
        if (!categories?.length) {
            return {
                parentIndex: new Map<number, number | null>(),
                labelIndex: new Map<number, string>(),
            };
        }
        const idx = makeParentIndex(categories);
        return { parentIndex: idx.parent, labelIndex: idx.label };
    }, [categories]);

    const getPathLabels = (
        categoryId: number,
        parent: Map<number, number | null>,
        labels: Map<number, string>
    ): string[] => {
        const out: string[] = [];
        let current: number | null | undefined = categoryId;
        const guard = new Set<number>();

        while (current != null && !guard.has(current)) {
            guard.add(current);
            out.unshift(labels.get(current) ?? String(current));
            current = parent.get(current) ?? null;
        }

        return out;
    };

    const categoryBreadcrumb = useMemo(() => {
        if (!artwork?.categoryId) return ".";
        return categories?.length
            ? getPathLabels(artwork.categoryId, parentIndex, labelIndex).join(" > ")
            : "";
    }, [artwork?.categoryId, categories, parentIndex, labelIndex]);

    const artistLifeSpan = useMemo(() => {
        if (!artwork?.artist) return null;

        const birthYear = artwork.artist.birthDate
            ? `${new Date(artwork.artist.birthDate).getFullYear()}`
            : null;

        const deathYear = artwork.artist.deathDate
            ? ` - ${new Date(artwork.artist.deathDate).getFullYear()}`
            : null;

        if (birthYear && deathYear) return `${birthYear}${deathYear}`;
        if (birthYear) return birthYear;
        if (deathYear) return deathYear;
        return null;
    }, [artwork]);

    const locationPath = useMemo(() => artwork?.locationPath ?? "", [artwork]);

    const dimensionsPreview = useMemo<DimensionsPreviewStruct>(() => {
        if (!artwork?.measurements?.length) {
            return {
                label: "Dimensions",
                value: ""
            };
        }

        const artworkMeasurement = artwork.measurements.find(
            m => m.measurementType === ArtworkMeasurementType.Artwork
        );

        const framedMeasurement = artwork.measurements.find(
            m => m.measurementType === ArtworkMeasurementType.Framed
        );

        const artworkValue = formatMeasurement(
            artworkMeasurement,
            dimensionUnitPreference
        );

        const framedValue = formatMeasurement(
            framedMeasurement,
            dimensionUnitPreference
        );

        switch (dimensionDisplayPreference) {
            case DimensionDisplayType.Framed:
                return {
                    label: "Dimensions",
                    value: (
                        <>
                            {framedValue && (
                                <div>
                                    <strong>Framed:</strong> {framedValue}
                                </div>
                            )}
                        </>
                    )
                };

            case DimensionDisplayType.Both: {
                return {
                    label: "Dimensions",
                    value: (
                        <>
                            {artworkValue && (
                                <div>
                                    <strong>Artwork:</strong> {artworkValue}
                                </div>
                            )}

                            {framedValue && (
                                <div>
                                    <strong>Framed:</strong> {framedValue}
                                </div>
                            )}
                        </>
                    )
                };
            }

            case DimensionDisplayType.Unframed:
            default:
                return {
                    label: "Dimensions",
                    value: (
                        <>
                            {artworkValue && (
                                <div>
                                    <strong>Artwork:</strong> {artworkValue}
                                </div>
                            )}
                        </>
                    )
                };
        }
    }, [
        artwork,
        dimensionDisplayPreference,
        dimensionUnitPreference
    ]);

    const normalizeDate = (raw: any) => {
        const formats = ["DD-MM-YYYY" , "YYYY-MM-DD"];
        const parsed = dayjs(raw, formats, true);
        return parsed.isValid() ? parsed.format("DD-MM-YYYY") : undefined;
    };

    const provenanceDetails = useMemo<ArtworkProvenance | undefined>(() => {
        if (!artwork) return undefined;

        const dateString = normalizeDate(artwork.dateAcquired);

        return {
            dateAcquired: dateString,
            sourceId: artwork.sourceId ?? 0,
            sourceName: artwork.sourceName,
            provenance: artwork.provenance ?? "",
            notes: artwork.sourceDescription ?? "",
        };
    }, [artwork]);

    const statusHeadline = useMemo(() => {
        const statusName = artworkStatus?.statusName ?? "";
        const statusDate = artworkStatus?.statusDate ?? "";
        console.log("Status Headline:", statusName, statusDate, artworkId);
        return statusName === "" ? "Not set" : `${statusName} set on ${formatDateOnly(statusDate)}`;
    }, [artworkStatus, artwork]);

    const handleOpenEditModal = () => setOpenEditArtworkModal(true);
    const handleEditArtworkClose = () => {
        setOpenEditArtworkModal(false);
        setReloadArtworkDetailsToken(prev => prev + 1);
    };

    const handleSaveComplete = () => {
        setOpenEditArtworkModal(false);
    };

    const handleOpenDelete = () => setIsDeleteOpen(true);
    const handleCloseDelete = () => {
        if (!isDeleting) {
            setIsDeleteOpen(false);
        }
    };
    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;

            await del(`artworks/${artworkId}`, accessToken);
            navigate("/gallery");
        } finally {
            setIsDeleting(false);
        }
    };
    const handleUploadSuccess = (_data: any) => { };
    const handleUploadResult = (_result: boolean, _message: string) => { };
    const handleUploadError = (_error: string) => { };
    const handleOpenMove = () => setShowCreate(true);

    //Dialogs
    const [valDialogOpen, setValDialogOpen] = useState(false);
    const [payDialogOpen, setPayDialogOpen] = useState(false);
    const [notesDialogOpen, setNotesDialogOpen] = useState(false);
    const [artworkRefDialogOpen, setArtworkRefDialogOpen] = useState(false);
    const [artworkRefEditMode, setArtworkRefEditMode] = useState(true);
    const [artworkProvenanceViewMode, setArtworkProvenanceViewMode] = useState<ProvenanceMode>("view");
    

    //Refresh Triggers
    const [triggers, setTriggers] = useState<RefreshTriggers>({
        valuation: 0,
        status: 0,
        reference: 0,
        provenance: 0,
        note: 0,
        document: 0,
        payment: 0,
    });

    const triggerRefresh = (key: TriggerKey) => {
        setTriggers(prev => ({
            ...prev,
            [key]: prev[key] + 1
        }));
    };

    const newArtReference: ArtworkReference = {
        artworkReferenceId: undefined,
        artworkId: artworkId,
        type: "Literature",
        details: "",
        dateText: "",
        url: "",
    }

    const handleOpenArtworkReference = () => {
        setArtworkRefDialogOpen(true);
        setArtworkRefEditMode(true);
    }

    const handleCloseArtworkReference = () => {
        setArtworkRefDialogOpen(false);
    }

    const handleEditArtworkProvenance = () => {
        setArtworkProvenanceViewMode("edit");
    }

    const handleCloseArtworkMovement = () => {
        setShowCreate(false);
    }

    const handleArtworkMovementCreated = () => {
        handleCloseArtworkMovement();
        setReloadStatusToken(prev => prev + 1);
        triggerRefresh('status');
    }

    const saveValuation = async (valuationData: FormData) => {
        const accessToken = await getAccessToken();
        if (!accessToken || !artwork) return;

        try {
            await axios.post(`${BASE_URL}/valuations/add`, valuationData, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "X-Client-Id": activeClientId?.toString() || "",
                    "Content-Type": "multipart/form-data",
                },
            });

            setReloadValuationsToken(prev => prev + 1);
            triggerRefresh('valuation');

        } catch (err) {
            console.error("Valuation update failed:", err);
        }
    };
    const handleSaveArtworkReference = async (ref: ArtworkReference) => {

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;

            const formData = new FormData();

            formData.append("ArtworkReferenceId", ref.artworkReferenceId?.toString() ?? "");
            formData.append("ArtworkId", artworkId.toString());
            formData.append("Type", ref.type);
            formData.append("Details", ref.details);

            if (ref.dateText) formData.append("DateText", ref.dateText);
            if (ref.url) formData.append("Url", ref.url);

            if (ref.artworkReferenceId) {
                await put(`artworkreference/${ref.artworkReferenceId}`, accessToken, formData);
            } else {
                await post(`artworkreference/${artworkId}`, accessToken, formData);
            }

            handleCloseArtworkReference();

            // Refresh Artwork References
            triggerRefresh('reference');

        } catch (e: any) {
            alert("Failed to save reference");
        }
    };
    const handleDeleteArtworkReference = async (ref: ArtworkReference) => {
        if (!ref.artworkReferenceId) return;
        const ok = window.confirm("Are you sure you want to delete this entry?");
        if (!ok) return;
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;

            await del(`artworkreference/${ref.artworkReferenceId}`, accessToken);
            ;
            handleCloseArtworkReference();

            // Refresh Artwork References
            triggerRefresh('reference');

        } catch (e: any) {
            alert("Failed to delete reference");
        }
    };
    const handleProvenance_Cancel = () => {
        setArtworkProvenanceViewMode("view");
    }
    const handleProvenanceUpdate = async (provenanceData: ArtworkProvenance | null) => {
        if (!artwork) return;

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;

            const formData = new FormData();
            const parsed = dayjs(provenanceData?.dateAcquired, ["DD-MM-YYYY", "YYYY-MM-DD"], true);
            const dateToUse = parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";

            formData.append("ArtworkId", artwork.artworkId.toString());
            formData.append("DateAcquired", dateToUse);
            formData.append("SourceId", provenanceData?.sourceId.toString() || "");
            formData.append("SourceOrganisationId", "");
            formData.append("SourceName", provenanceData?.sourceName || "");
            formData.append("SourceDescription", provenanceData?.notes || "");
            formData.append("Provenance", provenanceData?.provenance || "");

            await put(`provenance/${artwork.artworkId}`, accessToken, formData);

            // Refresh Artwork References
            triggerRefresh('provenance');
            setArtworkUpdated(prev => prev + 1);
            setArtworkProvenanceViewMode("view");

        } catch (e: any) {
            alert("Failed to update provenance data");
        }
    };
    const handleChangeNotesDialogState = (newState: boolean) => {
        setNotesDialogOpen(newState);
    }
    const handlePaymentUpdate = () => {
        setPayDialogOpen(false);
        triggerRefresh('payment');
    }

    const handleRefreshPayments = () => setReloadPaymentsToken(prev => prev + 1);

    const handleTabChange = (_: any, newValue: number) => setTabValue(newValue);
    const handleSelectChange = (event: SelectChangeEvent<number>) => {
        setTabValue(Number(event.target.value));
    };

    const uploadUrl = `${BASE_URL}/artworks/documents`;

    const formatReferenceNumber = (id: number): string => {
        return `AMH-${id.toString().padStart(6, '0')}`;
    };

    const tabs: ArtworkSupportingTab[] = useMemo(
        () => [
            {
                label: "Values",
                mobileLabel: "Values",
                hidden: hasFinanceRole != true,
                component: (
                    <ValuationsTabContent
                        artworkId={artworkId}
                        reloadToken={reloadValuationsToken}
                        onSave={saveValuation}
                    />
                ),
                actions: [
                    {
                        label: "Add New Value",
                        icon: <AddCircleOutlineIcon />,
                        variant: "outlined",
                        onClick: () => setValDialogOpen(true),
                        disabled: !canEditArtwork
                    }
                ]
            },
            {
                label: "Status",
                mobileLabel: "Status",
                hidden: false,
                component: (
                    <ArtworkStatusPanel
                        artworkId={artworkId}
                        onStatusChanged={(c) => console.log("status updated", c)}
                        refresh={triggers} 
                    />
                ),
            },
            {
                label: "References",
                mobileLabel: "References",
                component: (<ArtworkReferencesTabs artworkId={artworkId} refresh={triggers} />),
                hidden: false,
                actions: [
                    {
                        label: "Add Reference",
                        icon: <AddCircleOutlineIcon />,
                        variant: "outlined",
                        onClick: () => handleOpenArtworkReference(),
                        disabled: !canEditArtwork
                    }
                ]
            },
            {
                label: "Provenance",
                mobileLabel: "Provenance",
                component: (
                    <ProvenanceForm
                        initialValues={provenanceDetails}
                        mode={artworkProvenanceViewMode}
                        onSubmit={handleProvenanceUpdate}
                        onCancel={handleProvenance_Cancel}
                        showHeaderActions={true}
                    />
                ),
                hidden: false,
                actions: [
                    {
                        label: "Edit Provenance",
                        icon: <EditIcon />,
                        variant: "outlined",
                        onClick: () => handleEditArtworkProvenance(),
                        disabled: !canEditArtwork
                    }
                ]
            },
            {
                label: "Notes",
                mobileLabel: "Notes",
                component: <ArtworkNotes artworkId={artworkId} dialogOpen={notesDialogOpen} setOpenDialog={handleChangeNotesDialogState} />,
                hidden: false,
                actions: [
                    {
                        label: "Add Note",
                        icon: <AddCircleOutlineIcon />,
                        variant: "outlined",
                        onClick: () => setNotesDialogOpen(true),
                        disabled: !canEditArtwork
                    }
                ]
            },
            {
                label: "Documents",
                mobileLabel: "Documents",
                component: (
                    <FileUploader
                        apiUrl={uploadUrl}
                        artworkId={id}
                        acceptedFileTypes=".jpg,.png,.pdf,.doc,.docx,.xls,.xlsx"
                        onUploadSuccess={handleUploadSuccess}
                        onUploadError={handleUploadError}
                        uploadResult={handleUploadResult}
                        showDeleteButton={canEditArtwork}
                        viewOnly={isViewOnly}
                    />
                ),
                hidden: false,
            },
            {
                label: "Payments",
                mobileLabel: "Payments",
                component: (
                    <PaymentsTab
                        artwork={artwork}
                        reloadToken={reloadPaymentsToken}
                        onRefresh={handleRefreshPayments}
                    />
                ),
                hidden: hasFinanceRole != true,
                actions: [
                    {
                        label: "Add Payment Plan",
                        icon: <AddCircleOutlineIcon />,
                        variant: "outlined",
                        onClick: () => setPayDialogOpen(true),
                        disabled: !canEditArtwork 
                    }
                ]
            },
        ],
        [
            artwork,
            artworkId,
            artworkUpdate.artworkId,
            canEditArtwork,
            handleProvenanceUpdate,
            id,
            isViewOnly,
            provenanceDetails,
            reloadPaymentsToken,
            reloadValuationsToken,
        ]
    );


    const stillLoading = artworkReq.loading || documentsReq.loading;

    if (stillLoading) {
        return (
            <div className="details-container">
                <ArtworkPageSkeleton />
            </div>
        );
    }

    if (errorAny) {
        return (
            <div className="bg-yellow-50 border border-yellow-300 p-3 rounded">
                <p>{errorAny}</p>
                <button className="mt-2 px-3 py-1 rounded border" onClick={() => refresh()}>
                    Retry loading...
                </button>
            </div>
        );
    }

    if (error) return <Alert severity="error">{error}</Alert>;
    if (!artwork) return <Alert severity="warning">Artwork not found.</Alert>;

    const headerSection = (
        <ArtworkHeaderSection
            artwork={artwork}
            artistLifeSpan={artistLifeSpan}
        />
    );
    const quickFactsSection = (
        <ArtworkQuickFactsPanel
            items={[
                {
                    label: "Medium",
                    value: artwork.medium ?? "Not provided",
                    icon: <PaletteOutlinedIcon fontSize="small" />,
                },
                {
                    label: "Category",
                    value: categoryBreadcrumb,
                    icon: <CategoryOutlinedIcon fontSize="small" />,
                },
                {
                    label: "Location",
                    value: locationPath,
                    icon: <PlaceOutlinedIcon fontSize="small" />,
                },
                {
                    label: dimensionsPreview.label,
                    value: dimensionsPreview.value,
                    icon: <StraightenOutlinedIcon fontSize="small" />,
                },
                {
                    label: "Status",
                    value: statusHeadline,
                    icon: <InfoOutlinedIcon fontSize="small" />,
                },
            ]}
        />
    );
    const primaryMediaSection = (
        <ArtworkPrimaryMediaSection
            artworkId={artworkId}
            artworkTitle={artwork.artWorkTitle}
            isMobile={isMobile}
        />
    );
    const primaryDetailsSection = (
        <ArtworkDetailsSection
            artwork={artwork}
            categoryPath={categoryBreadcrumb}
            locationPath={locationPath}
            dimensionsPreview={dimensionsPreview}
            canEditArtwork={canEditArtwork ?? false}
            isAdmin={isAdmin ?? false}
            isViewOnly={isViewOnly}
            onEdit={handleOpenEditModal}
            onDelete={handleOpenDelete}
            onMove={handleOpenMove}
            statusHeadline={statusHeadline}
            additionalDetails={[
                { label: "Artist", value: artwork.artistName },
                { label: "Title", value: artwork.artWorkTitle },
                { label: "Medium", value: artwork.medium },
                { label: "Source", value: artwork.sourceDescription },
                { label: "Acquired", value: artwork?.dateAcquired ? dayjs(artwork.dateAcquired).format("DD MMM YYYY") : undefined },
            ]}
        />
    );
    const secondaryContentSection = (
        <ArtworkSupportingSection
            isMobile={isMobile}
            value={tabValue}
            onTabChange={handleTabChange}
            onSelectChange={handleSelectChange}
            tabs={tabs.map(tab => ({
                ...tab,
                label: (
                    <Badge
                        color="secondary"
                        sx={{ "& .MuiBadge-badge": { right: -16 } }}
                    >
                        {tab.label}
                    </Badge>
                ),
            }))}
        />
    );

    return (
        <>
            <div className="details-container">
                {isMobile ? (
                    <ResponsiveDetailLayout
                        header={headerSection}
                        primaryMedia={primaryMediaSection}
                        primaryDetails={
                            <>
                                <Box sx={{ mt: 2 }}>
                                    {primaryDetailsSection}
                                </Box>
                            </>
                        }
                        secondaryContent={secondaryContentSection}
                        spacing={2}
                    />
                ) : (
                    <>
                        {headerSection}

                            <Box sx={{ mt: 2 }}>
                                {quickFactsSection}
                            </Box>

                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "20px" }}>
                            <div className="artwork-page">
                                <div className="artwork-layout">
                                    {primaryMediaSection}
                                    {primaryDetailsSection}
                                    {secondaryContentSection}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <ArtworkManagement
                    isOpen={openEditArtworkModal}
                    onClose={handleEditArtworkClose}
                    onSaveComplete={handleSaveComplete}
                    artworkId={artworkId}
                />
            </div>

            <div>
                <ArtworkNavigation
                    currentArtworkId={currentArtworkId}
                    artworks={navigationArtworks}
                    iconVariant="standard"
                />
                <div style={{ height: "80px" }} />
            </div>

            <ConfirmDeleteArtworkDialog
                isOpen={isDeleteOpen}
                onClose={handleCloseDelete}
                onConfirm={handleConfirmDelete}
                artworkTitle={artwork.artWorkTitle ?? "Unknown"}
                artworkRef={artwork.artworkId.toString() ?? "N/A"}
            />

            <AddValuationDialog
                open={valDialogOpen}
                onClose={() => setValDialogOpen(false)}
                artworkId={artworkId}
                onSave={saveValuation}
                valuation={null}
            />

            <ArtworkPaymentScheduleDialog
                key={artwork.artworkId}
                open={payDialogOpen}
                artworkId={artwork.artworkId}
                artworkTitle={artwork.artWorkTitle}
                valuationDefault={0}
                currencyCodeDefault={"GBP"}
                onClose={() => setPayDialogOpen(false)}
                onCreated={(r) => {
                    handlePaymentUpdate();
                }}
            />

            <ArtworkReferencePanel
                reference={newArtReference}
                editMode={artworkRefEditMode}
                isOpen={artworkRefDialogOpen}
                isViewOnly={isViewOnly}
                setEditMode={setArtworkRefEditMode}
                onClose={handleCloseArtworkReference}
                onSave={handleSaveArtworkReference}
                onDelete={handleDeleteArtworkReference}
                onCancel={handleCloseArtworkReference}
            />

            {/* Record Artwork Movement (Status Update) */}
            {showCreate && (
                <CreateMovementModal
                    artworkId={artwork.artworkId}
                    artworkTitle={artwork.artWorkTitle}
                    artistName={artwork.artistName}
                    referenceNumber={formatReferenceNumber(artwork?.artworkId)}
                    thumbnailUrl={artwork.mainImage}
                    statuses={statuses}
                    onClose={handleCloseArtworkMovement}
                    onCreated={handleArtworkMovementCreated}
                />
            )}
        </>
    );
};

export default ArtworkPage;